
import React, { forwardRef } from 'react'

import {
  Icon,
  Button,
  ArrowLeft,
  ArrowRight,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, arrayOf } from 'prop-types'

import { useCaseEmbedShape } from "../../../../Utils/propTypes"
import MediaRenderer from '../../../MediaRenderer/MediaRenderer'

const MediaCarousel = forwardRef(({
  embeds,
  currentIndex,
  isGalleryView,
  isMobile,
  onPrevious,
  onNext,
  onDotClick,
  onMediaClick,
  isNavHidden = false,
  useLargeImage = false,
}, navContainerRef) => {
  const currentEmbed = embeds[currentIndex]
  const hasMultipleEmbeds = embeds.length > 1
  const isPreviousDisabled = !hasMultipleEmbeds || currentIndex === 0
  const isNextDisabled = !hasMultipleEmbeds || currentIndex >= embeds.length - 1
  const embedUrl = isGalleryView || useLargeImage
    ? currentEmbed.largeUrl || currentEmbed.url
    : currentEmbed.mediumUrl || currentEmbed.url

  return (
    <div
      ref={navContainerRef}
      className="use-cases-carousel"
      tabIndex={isGalleryView ? -1 : undefined}
      role={isGalleryView ? 'region' : undefined}
      aria-label={isGalleryView ? 'Use case media carousel' : undefined}
    >
      {!isNavHidden && hasMultipleEmbeds && (
        <Button
          aria-label="Previous media"
          customClass="use-cases-carousel__btn-prev"
          disabled={isPreviousDisabled}
          icon={<Icon svgComponent={ArrowLeft} color="dark" />}
          onClick={onPrevious}
          type="ghost"
          variant="only-icon"
        />
      )}
      <div
        className="use-cases-carousel__media"
        {...(onMediaClick && { onClick: onMediaClick })}
        style={{
          pointerEvents: isGalleryView || isMobile ? 'auto' : 'none',
        }}
      >
        <MediaRenderer
          url={embedUrl}
          fallbackUrl={currentEmbed.url}
          file={currentEmbed.file}
          type={currentEmbed.type}
          externalId={currentEmbed.externalId}
          source={currentEmbed.source}
          isGalleryView={isGalleryView}
        />
      </div>
      {!isNavHidden && hasMultipleEmbeds && (
        <Button
          aria-label="Next media"
          customClass="use-cases-carousel__btn-next"
          disabled={isNextDisabled}
          icon={<Icon svgComponent={ArrowRight} color="dark" />}
          onClick={onNext}
          type="ghost"
          variant="only-icon"
        />
      )}
      {!isNavHidden && hasMultipleEmbeds && (
        <div className="use-cases-carousel__dots">
          {embeds.map((_, index) => (
            <button
              key={index}
              className={`use-cases-carousel__dot ${index === currentIndex ? 'active' : ''}`}
              onClick={(e) => onDotClick(e, index)}
              aria-label={`Go to media ${index + 1} of ${embeds.length}`}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  )
})

MediaCarousel.propTypes = {
  currentIndex: number.isRequired,
  isGalleryView: bool.isRequired,
  isMobile: bool.isRequired,
  onPrevious: func.isRequired,
  onNext: func.isRequired,
  onDotClick: func.isRequired,
  onMediaClick: func,
  embeds: arrayOf(useCaseEmbedShape).isRequired,
  isNavHidden: bool,
  useLargeImage: bool,
}

MediaCarousel.displayName = 'MediaCarousel'

export default MediaCarousel
